/**
 * Authority Management API
 * 권한 설정 관련 API
 */
import { AxiosError } from 'axios';
import { apiClient } from '@/shared/api/client';
import {
    Authority,
    CreateAuthorityRequest,
    UpdateAuthorityRequest,
    AuthorityResponse,
} from '../types/authority.types';
import { CommonCodeItem, CommonCodeListResponse } from '../utils/authorityUtils';

export const authorityApi = {
    /**
     * 권한 목록 조회
     * GET /api/v1/common-code/list?grpCode=PRM
     */
    getAuthorityList: async (includeStandard = true): Promise<Authority[]> => {
        try {
            const response = await apiClient.get<CommonCodeListResponse>(
                '/api/v1/common-code/list',
                { params: { grpCode: 'PRM' } }
            );

            const authorities: Authority[] = response.data.codes.map((code: CommonCodeItem) => ({
                prmCode: code.comCode,
                prmName: code.comCdName,
                prmDesc: code.val1 || '',
                baseUserType: code.val3 || '',
                accessPages: code.val2 ? code.val2.split(',').map(p => p.trim()) : [],
                accessPagesStr: code.val2 || '',
                insDate: code.insDate,
                insUser: code.insUser,
                updDate: code.updDate,
                updUser: code.updUser,
            }));

            if (includeStandard) return authorities;
            return authorities.filter(auth => !['A', 'C', 'S', 'R'].includes(auth.prmCode));
        } catch (error) {
            console.error('권한 목록 조회 실패:', error);
            return [];
        }
    },

    /**
     * 권한 상세 조회
     */
    getAuthority: async (prmCode: string): Promise<Authority | null> => {
        try {
            const authorities = await authorityApi.getAuthorityList(true);
            return authorities.find(auth => auth.prmCode === prmCode) || null;
        } catch (error) {
            console.error('권한 상세 조회 실패:', error);
            return null;
        }
    },

    /**
     * 권한 등록
     * POST /api/v1/system/permission/regist
     */
    createAuthority: async (request: CreateAuthorityRequest): Promise<AuthorityResponse> => {
        try {
            const response = await apiClient.post<AuthorityResponse>(
                '/api/v1/system/permission/regist',
                request
            );
            return response.data;
        } catch (error: unknown) {
            let errorMessage = '권한 등록에 실패했습니다.';

            if (error instanceof Error) errorMessage = error.message;
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as AxiosError<AuthorityResponse>;
                if (axiosError.response?.data?.message) errorMessage = axiosError.response.data.message;
            }

            return { prmCode: request.prmCode, success: false, message: errorMessage };
        }
    },

    /**
     * 권한 수정
     * POST /api/v1/system/permission/update
     */
    updateAuthority: async (request: UpdateAuthorityRequest): Promise<AuthorityResponse> => {
        try {
            const response = await apiClient.post<AuthorityResponse>(
                '/api/v1/system/permission/update',
                request
            );
            return response.data;
        } catch (error: unknown) {
            let errorMessage = '권한 수정에 실패했습니다.';

            if (error instanceof Error) errorMessage = error.message;
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as AxiosError<AuthorityResponse>;
                if (axiosError.response?.data?.message) errorMessage = axiosError.response.data.message;
            }

            return { prmCode: request.prmCode, success: false, message: errorMessage };
        }
    },

    /**
     * 권한 삭제
     * DELETE /api/v1/system/permission/{prmCode}
     */
    deleteAuthority: async (prmCode: string, deleteUserId: string): Promise<AuthorityResponse> => {
        try {
            const response = await apiClient.delete<AuthorityResponse>(
                `/api/v1/system/permission/${prmCode}`,
                { params: { deleteUserId } }
            );
            return response.data;
        } catch (error: unknown) {
            let errorMessage = '권한 삭제에 실패했습니다.';

            if (error instanceof Error) errorMessage = error.message;
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as AxiosError<AuthorityResponse>;
                if (axiosError.response?.data?.message) errorMessage = axiosError.response.data.message;
            }

            return { prmCode, success: false, message: errorMessage };
        }
    },
};
